import { INFRA_ELEMENT_TYPES_CA } from './infrastructureElementTypes_CA';
import { INFRA_ELEMENT_TYPES_US } from './infrastructureElementTypes_US';

export { INFRA_ELEMENT_TYPES_CA, INFRA_ELEMENT_TYPES_US };

export const DESIGN_STANDARD_OPTIONS_CA = [
  { value: 'municipal', label: 'Municipal design standards' },
  { value: 'provincial_highway', label: 'Provincial highway (MTO / MOT)' },
  { value: 'nbc_site', label: 'NBC site provisions' },
  { value: 'provincial_environmental', label: 'Provincial environmental' },
];

export const DESIGN_STANDARD_OPTIONS_US = [
  { value: 'municipal', label: 'Municipal design standards' },
  { value: 'state_dot', label: 'State DOT' },
  { value: 'ibc_site', label: 'IBC / adopted local code (site)' },
  { value: 'epa_npdes', label: 'EPA NPDES / state environmental' },
];

export const MATERIAL_CONTEXT_OPTIONS = [
  { value: 'asphalt', label: 'Asphalt pavement' },
  { value: 'concrete', label: 'Concrete pavement / structures' },
  { value: 'pvc', label: 'PVC pipe' },
  { value: 'hdpe', label: 'HDPE pipe' },
  { value: 'rcp', label: 'Reinforced concrete pipe (RCP)' },
  { value: 'earthworks', label: 'Earthworks / granular' },
  { value: 'other', label: 'Other / mixed' },
];

export function getInfraTypesForRegion(region) {
  return region === 'US' ? INFRA_ELEMENT_TYPES_US : INFRA_ELEMENT_TYPES_CA;
}

export function getDesignStandardOptions(region) {
  return region === 'US' ? DESIGN_STANDARD_OPTIONS_US : DESIGN_STANDARD_OPTIONS_CA;
}

export function getDesignStandardLabel(value, region) {
  if (!value) return '';
  const opt = getDesignStandardOptions(region).find((o) => o.value === value);
  return opt?.label || value;
}

function buildInfraMap(region) {
  const m = new Map();
  for (const [key, title, desc, code, family] of getInfraTypesForRegion(region)) {
    m.set(key, { key, title, desc, code, family });
  }
  return m;
}

const mapsByRegion = { CA: buildInfraMap('CA'), US: buildInfraMap('US') };

export function infraByKey(key, region = 'CA') {
  const r = region === 'US' ? 'US' : 'CA';
  return mapsByRegion[r].get(key) || null;
}

export function getInfraFamily(key) {
  if (!key) return '';
  const entry = infraByKey(key, 'CA') || infraByKey(key, 'US');
  return entry?.family || '';
}

export function formatInfraTypesLabel(keys, region = 'CA') {
  if (!keys?.length) return '';
  return keys
    .map((k) => infraByKey(k, region)?.title || k)
    .filter(Boolean)
    .join(', ');
}

export function getInfraUnitLabels(occupancyRegion) {
  const isCA = occupancyRegion === 'CA';
  return {
    areaLabel: isCA ? 'm²' : 'ft²',
    linearLabel: isCA ? 'm' : 'ft',
    longLinearLabel: isCA ? 'km' : 'mi',
    largeAreaLabel: isCA ? 'ha' : 'acres',
  };
}

/** Resolve a legacy free-text infraType to a canonical key when possible. */
export function resolveLegacyInfraTypeKey(infraType) {
  const raw = (infraType || '').trim();
  if (!raw) return '';
  const slug = raw.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  if (infraByKey(slug, 'CA')) return slug;
  const lists = [INFRA_ELEMENT_TYPES_CA, INFRA_ELEMENT_TYPES_US];
  for (const list of lists) {
    for (const [key, title] of list) {
      if (title.toLowerCase() === raw.toLowerCase()) return key;
      if (key === slug) return key;
    }
  }
  return slug || raw;
}

export function getInfraMetricsProfile(entity) {
  const primary = entity?.primaryInfraType || entity?.infraTypes?.[0] || '';
  const family = entity?.infraFamily || getInfraFamily(primary);
  const base = {
    family,
    showLinearExtent: false,
    showArea: false,
    showRowWidth: false,
    showNetworkDiameter: false,
    showBurialDepth: false,
    requireLinearExtent: false,
    requireArea: false,
    requireMaterialContext: true,
  };
  switch (family) {
    case 'linear':
      return { ...base, showLinearExtent: true, showRowWidth: true, requireLinearExtent: true };
    case 'area':
      return { ...base, showArea: true, requireArea: true };
    case 'point':
      return { ...base, showArea: true };
    case 'network':
      return {
        ...base,
        showLinearExtent: true,
        showNetworkDiameter: true,
        showBurialDepth: true,
        requireLinearExtent: true,
      };
    default:
      return { ...base, showLinearExtent: true, showArea: true, requireMaterialContext: false };
  }
}
